import { useState } from 'react'
import {
  formatCompactCap,
  formatFundingDisplay,
  formatMarginPct,
  formatUsdPrice,
} from '../../data/fundingMarketRisk'
import { ChevronIcon, StarIcon } from './icons'

const CARD_CLASS =
  'flex w-full flex-col overflow-hidden rounded-panel border-[0.5px] border-line bg-fill shadow-[inset_0_var(--hl-y)_0_0_var(--hl-top),0px_0px_0px_0.5px_var(--ring-crisp),0px_1px_0px_0px_var(--edge-under)] light:bg-surface light:shadow-[inset_0_0_0_0.5px_var(--ring-crisp)]'

function cellClass(index) {
  return [
    'flex min-w-0 flex-col gap-0.5 px-4 py-2.5',
    index % 2 === 1 ? 'border-l-[0.5px] border-line' : '',
    index >= 2 ? 'border-t-[0.5px] border-line' : '',
  ]
    .filter(Boolean)
    .join(' ')
}

export default function RiskCards({ markets, favorites, onToggleFavorite }) {
  const [openSymbol, setOpenSymbol] = useState(null)

  return (
    <div className="hidden flex-col gap-2.5 max-mobile:flex">
      {markets.map((market) => {
        const open = openSymbol === market.symbol
        const details = [
          {
            label: 'Index Price',
            value: formatUsdPrice(market.indexPrice, market.priceDecimals),
          },
          {
            label: 'Open Interest Cap',
            value: formatCompactCap(market.openInterestCap),
          },
          {
            label: 'Maintenance Margin',
            value: formatMarginPct(market.maintenanceMargin),
          },
          {
            label: 'Min Funding',
            value: formatFundingDisplay(market.minFunding),
            valueClass: 'text-down',
          },
          {
            label: 'Max Funding',
            value: formatFundingDisplay(market.maxFunding),
            valueClass: 'text-up',
          },
        ]
        return (
          <div
            key={market.symbol}
            data-state={open ? 'open' : 'closed'}
            className={CARD_CLASS}
          >
            <div
              role="button"
              tabIndex={0}
              className="group flex w-full cursor-pointer items-center gap-3 p-4 text-base text-ink select-none active:bg-ink/4 focus-visible:[outline:2px_solid_var(--color-ink)] focus-visible:outline-offset-2"
              aria-expanded={open ? 'true' : 'false'}
              data-state={open ? 'open' : 'closed'}
              onClick={() =>
                setOpenSymbol((current) =>
                  current === market.symbol ? null : market.symbol
                )
              }
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  setOpenSymbol((current) =>
                    current === market.symbol ? null : market.symbol
                  )
                }
              }}
            >
              <div className="flex min-w-0 shrink flex-col gap-1">
                <div
                  className="flex w-full min-w-0 items-center gap-2 overflow-hidden"
                  data-testid="market-selector-table-cell"
                >
                  <button
                    type="button"
                    className={`inline-flex items-center gap-2 text-sm/sm font-medium disabled:opacity-50 disabled:no-underline shrink-0 rounded-pill p-1 no-underline hover:bg-ink/10 ${
                      favorites.has(market.symbol)
                        ? 'text-ink hover:text-ink'
                        : 'text-muted hover:text-meta active:text-meta'
                    }`}
                    onClick={(event) => {
                      event.stopPropagation()
                      onToggleFavorite(market.symbol)
                    }}
                  >
                    <span
                      data-icon="true"
                      className="flex shrink-0 items-center justify-center size-4"
                    >
                      <StarIcon />
                    </span>
                  </button>
                  <div className="flex min-w-0 items-center gap-2">
                    <div className="flex size-5 shrink-0 items-center justify-center">
                      {market.logoUrl ? (
                        <img
                          className="size-5 shrink-0"
                          src={market.logoUrl}
                          alt=""
                        />
                      ) : null}
                    </div>
                    <span
                      className="text-sm/sm text-ink shrink-0"
                      data-testid="market-card-symbol"
                    >
                      {market.symbol}
                    </span>
                    {market.leverage ? (
                      <span className="shrink-0 text-sm/sm text-ink/50 tabular-nums">
                        {market.leverage}x
                      </span>
                    ) : null}
                  </div>
                </div>
              </div>
              <div className="ml-auto flex min-w-0 shrink-[0.4] flex-col items-end gap-0.5 text-right">
                <span className="font-mono label-meta text-muted uppercase">
                  Mark Price
                </span>
                <span className="tabular-nums">
                  {formatUsdPrice(market.markPrice, market.priceDecimals)}
                </span>
              </div>
              <span className="flex size-4 shrink-0 items-center justify-center text-faint transition-transform duration-200 group-data-[state=open]:rotate-180">
                <span
                  data-icon="true"
                  className="flex shrink-0 items-center justify-center size-3.5"
                >
                  <ChevronIcon />
                </span>
              </span>
            </div>
            <div
              data-state={open ? 'open' : 'closed'}
              hidden={!open}
              className="overflow-hidden"
            >
              <div className="grid grid-cols-2 border-t-[0.5px] border-line">
                {details.map((row, index) => (
                  <div key={row.label} className={cellClass(index)}>
                    <span className="font-mono label-meta text-muted uppercase">
                      {row.label}
                    </span>
                    <span
                      className={`text-xl/2xl tabular-nums ${row.valueClass || ''}`}
                    >
                      {row.value}
                    </span>
                  </div>
                ))}
                {details.length % 2 === 1 ? (
                  <div
                    aria-hidden="true"
                    className="border-l-[0.5px] border-line border-t-[0.5px] border-line"
                  />
                ) : null}
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}
